import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { usePermission } from '../../../../hooks/usePermission';
import { AdvancedFilter } from '../../../../interfaces/interfaces';
import { fetchAndSetAdvancedFilters, fetchAndSetBooths } from '../../../../reduxToolkit/slices/commonSlice';
import { useDispatch, useSelector } from '../../../../reduxToolkit/typedRedux';
import { getAllFilterCategories, getBooth, updateFilterCategories } from '../../../../services/services';

interface BoothFilterSelectProps {
  booths: string[];
  onlyPublic?: boolean;
  onSave?: () => void;
  // Option to activate all hidden child filters
  canShowAllFilters?: boolean;
  showHiddenFilters?: boolean;
}

export const BoothFilterSelect = (props: BoothFilterSelectProps) => {
  const { booths, onSave, onlyPublic, canShowAllFilters = false, showHiddenFilters = false } = props;
  const { t } = useTranslation();

  const event = useSelector((state) => state.common.event);

  const [filters, setFilters] = useState<AdvancedFilter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mainCheckedValues, setMainCheckedValues] = useState<Set<string>>(new Set<string>());
  const [normalCheckedValues, setNormalCheckedValues] = useState<Set<string>>(new Set<string>());
  const [visibleFilters, setVisibleFilters] = useState<Set<string>>(new Set<string>());
  const [showAllFilters, setShowAllFilters] = useState<boolean>(false);

  const dispatch = useDispatch();
  const { isEventHost } = usePermission();

  function fetch() {
    if (!event) return;
    if (!booths) return;
    setIsLoading(true);

    getBooth({ boothid: booths[0] }).then((data) => {
      setMainCheckedValues(
        new Set(
          data.categoryFilterValues.filter((el: any) => el.parentCategoryFilter.isMainFilter || el.parentCategoryFilter.isHiddenFilter).map((el: any) => el._id)
        )
      );
      setNormalCheckedValues(new Set(data.categoryFilterValues.filter((el: any) => !el.parentCategoryFilter.isMainFilter).map((el: any) => el._id)));
      const set = visibleFilters;
      data.categoryFilterValues.forEach((filterValue: any) => {
        if (filterValue.childCategoryFilter && filterValue.childCategoryFilter.length > 0) {
          filterValue.childCategoryFilter.forEach((childFilter: any) => {
            set.add(childFilter);
          });
        }
      });
      setVisibleFilters(set);
      getAllFilters();
    });
  }

  async function getAllFilters() {
    let mdata = {};

    if (!event) return;

    setIsLoading(true);
    try {
      const _filters = await getAllFilterCategories(event._id, onlyPublic);
      setFilters(_filters);
      mdata = _filters;
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);

    return mdata;
  }
  async function updateBooth() {
    if (!event) return;
    setIsLoading(true);
    await updateFilterCategories(booths, [...Array.from(mainCheckedValues), ...Array.from(normalCheckedValues)]);
    dispatch(fetchAndSetAdvancedFilters(event._id));
    dispatch(fetchAndSetBooths(event.slug));
    getAllFilters();
    if (onSave) onSave();
  }

  useEffect(() => {
    fetch();
  }, [event]);

  function onMainValueCheck(filterValue: any) {
    const arr = mainCheckedValues;
    if (mainCheckedValues.has(filterValue._id)) arr.delete(filterValue._id);
    else arr.add(filterValue._id);
    setMainCheckedValues(arr);

    const set = new Set<string>();

    Array.from(arr).map((filterValue: any) => {
      console.log(`Working ${filterValue}`);
      const filt: any = filters.find((filter: any) => {
        return filter.categoryFilterValues.map((el: any) => el._id).includes(filterValue);
      });

      if (filt)
        filt.categoryFilterValues.forEach((element: any) => {
          if (element._id === filterValue && element.childCategoryFilter)
            element.childCategoryFilter.forEach((childFilter: any) => {
              set.add(childFilter);
            });
        });
    });

    const n_arr = normalCheckedValues;

    filters.forEach((filter: any) => {
      if (!visibleFilters.has(filter._id)) {
        filter.categoryFilterValues.forEach((filterValue: any) => {
          n_arr.delete(filterValue._id);
        });
      }
    });
    setNormalCheckedValues(n_arr);
    setVisibleFilters(set);
  }

  function onNormalValueCheck(filterValue: any) {
    const arr = normalCheckedValues;
    if (normalCheckedValues.has(filterValue._id)) arr.delete(filterValue._id);
    else arr.add(filterValue._id);
    setNormalCheckedValues(arr);
  }

  return (
    <>
      {isLoading && (
        <div>
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              right: 0,
              background: '#FFFFFF',
              opacity: '0.7',
              zIndex: 2,
            }}
          />
          <CircularProgress style={{ position: 'absolute', top: '50%', right: '50%', color: 'var(--vystem-blue)', zIndex: 3 }} />
        </div>
      )}
      {!isLoading && event && filters && filters.length > 0 && (
        <div className="relative bg-white">
          <>
            <div className="flex flex-row justify-end w-full space-x-4">
              {canShowAllFilters && (
                <FormControlLabel
                  control={<Switch checked={showAllFilters} onChange={(e) => setShowAllFilters(e.target.checked)} />}
                  label={t('account:show-all-filters')}
                />
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', width: '90%', justifyContent: 'center' }}>
              <p style={{ fontWeight: 'normal', color: 'grey' }}>{t('account:select-filter-values-message')}</p>
              <h3>{t('account:main-filter')}</h3>

              {filters.map(
                (filter: any) =>
                  filter.isMainFilter && (
                    <div style={{ margin: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <p style={{ fontSize: '24px' }}>{filter.exhibitorviewname ? filter.exhibitorviewname : filter.name}</p>
                      {filter.categoryFilterValues.map((el: any) => (
                        <FormControlLabel
                          key={el._id}
                          control={
                            <Checkbox
                              defaultChecked={mainCheckedValues.has(el._id)}
                              style={{ color: 'var(--vystem-blue)' }}
                              onChange={() => onMainValueCheck(el)}
                            />
                          }
                          label={el.value}
                        />
                      ))}
                    </div>
                  )
              )}
            </div>
            {visibleFilters.size > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', width: '90%', justifyContent: 'center' }}>
                {filters
                  .filter((f: any) => !f.isMainFilter && !f.isHiddenFilter)
                  .map(
                    (filter: any) =>
                      visibleFilters.has(filter._id) && (
                        <div style={{ margin: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <h3>{filter.isMainFilter ? t('account:main-filter') : t('account:normal-filter')}</h3>
                          <p style={{ fontSize: '24px' }}>{filter.exhibitorviewname ? filter.exhibitorviewname : filter.name}</p>
                          {filter.categoryFilterValues.map((el: any) => (
                            <FormControlLabel
                              key={el._id}
                              control={
                                <Checkbox
                                  defaultChecked={normalCheckedValues.has(el._id)}
                                  style={{ color: 'var(--vystem-blue)' }}
                                  onChange={() => onNormalValueCheck(el)}
                                />
                              }
                              label={el.value}
                            />
                          ))}
                        </div>
                      )
                  )}
              </div>
            )}
            {(showHiddenFilters || showAllFilters || isEventHost()) && (
              <div style={{ display: 'flex', flexDirection: 'column', width: '90%', justifyContent: 'center' }}>
                <h3>{t('account:hidden-filters')}</h3>

                {filters.map(
                  (filter: any) =>
                    filter.isHiddenFilter && (
                      <div style={{ margin: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <p style={{ fontSize: '24px' }}>{filter.exhibitorviewname ? filter.exhibitorviewname : filter.name}</p>
                        {filter.categoryFilterValues.map((el: any) => (
                          <FormControlLabel
                            key={el._id}
                            control={
                              <Checkbox
                                defaultChecked={mainCheckedValues.has(el._id)}
                                style={{ color: 'var(--vystem-blue)' }}
                                onChange={() => onMainValueCheck(el)}
                              />
                            }
                            label={el.value}
                          />
                        ))}
                      </div>
                    )
                )}
              </div>
            )}
          </>
          <div className="sticky bottom-0 grid w-full py-4 bg-white place-items-center">
            <Button
              startIcon={<FontAwesomeIcon icon={faSave} style={{ margin: '10px' }} />}
              style={{
                color: 'white',
                background: 'var(--vystem-blue)',
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingRight: '30px',
                paddingLeft: '30px',
                fontSize: '15px',
              }}
              onClick={() => updateBooth()}>
              {t('common:save')}
            </Button>
          </div>
        </div>
      )}
      {!isLoading && (!filters || (filters && filters.length === 0)) && <p>{t('account:no-filters-available')}</p>}
    </>
  );
};
